// 表示用シェーダ: 合成結果 (ストレートアルファ) をチェッカーボードの上に描く。
// チェッカーはスクリーン空間で固定サイズ (Photoshop と同じ挙動)。

export const VIEW_FRAG = /* glsl */ `#version 300 es
precision highp float;
in vec2 vPos;                 // 文書座標
uniform sampler2D uComposite;
uniform vec2 uDocSize;
uniform float uCheckerSize;   // デバイス px
uniform vec3 uCheckerA;
uniform vec3 uCheckerB;
out vec4 outColor;
void main() {
  vec4 c = texture(uComposite, vPos / uDocSize);
  vec2 cell = floor(gl_FragCoord.xy / uCheckerSize);
  float k = mod(cell.x + cell.y, 2.0);
  vec3 checker = mix(uCheckerA, uCheckerB, k);
  vec3 rgb = c.rgb * c.a + checker * (1.0 - c.a);
  outColor = vec4(rgb, 1.0);
}
`

/** 単色クリア用 */
export const SOLID_FRAG = /* glsl */ `#version 300 es
precision highp float;
in vec2 vPos;
uniform vec4 uColor;
out vec4 outColor;
void main() { outColor = uColor; }
`

/** 2 枚のテクスチャを係数で混ぜる (pass-through グループの不透明度 / マスク用): out = mix(dst, src, k) */
export const MIX_FRAG = /* glsl */ `#version 300 es
precision highp float;
in vec2 vPos;
uniform sampler2D uDst;
uniform sampler2D uSrc;
uniform sampler2D uMask;
uniform vec4 uMaskRect;
uniform int uHasMask;
uniform vec2 uDocSize;
uniform float uOpacity;
out vec4 outColor;
void main() {
  vec2 uv = vPos / uDocSize;
  vec4 d = texture(uDst, uv);
  vec4 s = texture(uSrc, uv);
  float m = 1.0;
  if (uHasMask == 1) {
    vec2 uvM = (vPos - uMaskRect.xy) / uMaskRect.zw;
    m = (uvM.x >= 0.0 && uvM.x <= 1.0 && uvM.y >= 0.0 && uvM.y <= 1.0) ? texture(uMask, uvM).r : 0.0;
  }
  float k = clamp(uOpacity * m, 0.0, 1.0);
  // ストレートアルファ同士の線形補間はプリマルチで行う
  vec4 dp = vec4(d.rgb * d.a, d.a);
  vec4 sp = vec4(s.rgb * s.a, s.a);
  vec4 o = mix(dp, sp, k);
  outColor = o.a > 0.0 ? vec4(o.rgb / o.a, o.a) : vec4(0.0);
}
`

/**
 * マーチングアンツ。選択マスク (R8) を文書座標でサンプルし、しきい値 0.5 の等高線を
 * スクリーン空間 1px 幅の破線で描く。周囲 1 スクリーン px の近傍と比較して輪郭を出す。
 */
export const ANTS_FRAG = /* glsl */ `#version 300 es
precision highp float;
in vec2 vPos;                // 文書座標
uniform sampler2D uMask;
uniform vec4 uMaskRect;
uniform float uZoom;         // 文書 px あたりのデバイス px
uniform float uPhase;        // 0..7
out vec4 outColor;
float sampleMask(vec2 p) {
  vec2 uv = (p - uMaskRect.xy) / uMaskRect.zw;
  if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) return 0.0;
  return texture(uMask, uv).r;
}
void main() {
  float d = 1.0 / uZoom;   // 1 デバイス px を文書座標で
  float c = sampleMask(vPos);
  float inside = step(0.5, c);
  float n = 0.0;
  n += step(0.5, sampleMask(vPos + vec2(d, 0.0)));
  n += step(0.5, sampleMask(vPos - vec2(d, 0.0)));
  n += step(0.5, sampleMask(vPos + vec2(0.0, d)));
  n += step(0.5, sampleMask(vPos - vec2(0.0, d)));
  // 内側で、4 近傍のどれかが外側 → 輪郭
  float edge = inside * step(0.5, 4.0 - n);
  if (edge < 0.5) discard;
  float t = floor((gl_FragCoord.x + gl_FragCoord.y + uPhase) / 4.0);
  float k = mod(t, 2.0);
  outColor = vec4(vec3(k), 1.0);
}
`

/** クイックマスク: 選択マスク外を赤 50% で覆う。ライブのカバレッジ (加算/減算) も反映 */
export const QUICKMASK_FRAG = /* glsl */ `#version 300 es
precision highp float;
in vec2 vPos;
uniform sampler2D uMask;
uniform vec4 uMaskRect;
uniform int uHasMask;
uniform sampler2D uLive;
uniform vec2 uDocSize;
uniform int uHasLive;
uniform int uSubtract;
out vec4 outColor;
void main() {
  float m = 0.0;
  if (uHasMask == 1) {
    vec2 uv = (vPos - uMaskRect.xy) / uMaskRect.zw;
    if (uv.x >= 0.0 && uv.x <= 1.0 && uv.y >= 0.0 && uv.y <= 1.0) m = texture(uMask, uv).r;
  }
  if (uHasLive == 1) {
    float c = texture(uLive, vPos / uDocSize).r;
    m = uSubtract == 1 ? min(m, 1.0 - c) : max(m, c);
  }
  outColor = vec4(1.0, 0.0, 0.0, (1.0 - m) * 0.5);
}
`
